/**
 * Avatar + display label for a comment author.
 * Renders the agent portrait when the author is an agent sentinel user
 * ("system:<slug>" — Lev, Yuri, …), otherwise a User row, otherwise an
 * anonymous viewer label derived from email/seed.
 *
 * Visual matches the canvas share-comment pin (seeded HSL background +
 * white initial + ring) so block-level comments and canvas pins render
 * identically.
 */
import Image from "next/image";

import { agentAvatarImage, agentSlugFromSentinelUserId } from "@/constants/agents";
import { LEV_USER_ID } from "@/lib/levai/constants";
import {
  shareCommentAuthorInitial,
  shareCommentAvatarBackground,
} from "@/lib/shareComments/avatar";

export type CommentAuthorAvatarSize = "sm" | "md";

export type CommentAuthorAvatarProps = {
  authorUser: { id: string; name: string | null; imageUrl: string | null } | null;
  fallbackLabel?: string;
  /** Override the seed used for the HSL fallback background. Defaults to
   *  authorUser.id ?? authorUser.name ?? fallbackLabel. Useful when the
   *  display label and the stable identifier diverge — e.g. anonymous
   *  share-comment authors where the seed is a hash of email. */
  seed?: string;
  /** "sm" = 24px (default; mention autocomplete, notification bell).
   *  "md" = 28px matches the canvas comment pin avatar. */
  size?: CommentAuthorAvatarSize;
};

const SIZE_CLASSES: Record<CommentAuthorAvatarSize, { box: string; pixels: number; text: string }> = {
  sm: { box: "h-6 w-6", pixels: 24, text: "text-[10px]" },
  md: { box: "h-7 w-7", pixels: 28, text: "text-[11px]" },
};

export function CommentAuthorAvatar({
  authorUser,
  fallbackLabel,
  seed: seedOverride,
  size = "sm",
}: CommentAuthorAvatarProps) {
  const sizeClasses = SIZE_CLASSES[size];
  // Agents author under a "system:<slug>" sentinel user. Render that agent's
  // own portrait so each teammate is recognisable (LEV-1044 reviewer note 2 for
  // Lev; per-slug lookup so Yuri isn't shown as Lev).
  const agentImage = agentAvatarImage(agentSlugFromSentinelUserId(authorUser?.id));
  if (agentImage) {
    return (
      <Image
        src={agentImage}
        alt={authorUser?.name ?? "Agent"}
        width={sizeClasses.pixels}
        height={sizeClasses.pixels}
        className={`flex-shrink-0 rounded-full object-cover ${sizeClasses.box}`}
      />
    );
  }

  if (authorUser?.imageUrl) {
    return (
      <Image
        src={authorUser.imageUrl}
        alt={authorUser.name ?? "User"}
        width={sizeClasses.pixels}
        height={sizeClasses.pixels}
        className={`flex-shrink-0 rounded-full object-cover ${sizeClasses.box}`}
      />
    );
  }

  const label = authorUser?.name ?? fallbackLabel ?? "?";
  const seed = seedOverride ?? authorUser?.id ?? authorUser?.name ?? fallbackLabel ?? "anonymous";
  return (
    <span
      aria-label={label}
      style={{ backgroundColor: shareCommentAvatarBackground(seed) }}
      className={`flex flex-shrink-0 cursor-default items-center justify-center rounded-full border border-white font-semibold text-white shadow ring-1 ring-black/10 ${sizeClasses.box} ${sizeClasses.text}`}
    >
      {shareCommentAuthorInitial(label)}
    </span>
  );
}

export function commentAuthorDisplayName(
  authorUser: { id: string; name: string | null; email?: string | null } | null,
  fallback?: string,
): string {
  if (authorUser?.id === LEV_USER_ID) return "Lev";
  if (authorUser?.name) return authorUser.name;
  if (authorUser?.email) return authorUser.email;
  return fallback ?? "Anonymous";
}
